import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Grid, Stack } from "@mui/material";
import { Sort } from "@mui/icons-material";

import ExploreAppBar from "./components/ExploreAppBar";
import ExploreBanner, { SearchInput } from "./components/ExploreBanner";
import ExploreTagList from "./components/ExploreTagList";
import WheelExploreCard from "./components/WheelExploreCard";
import MultiSelect from "../../components/WheelGallery/WheelListing/ListComponents/MultiSelect";
import { TextView } from "../../components";
import FilterIcon from "../../assets/icons/Filter";

import {
  useInfiniteExploreWheels,
  useTopTags,
  useInfiniteExploreCreators,
} from "../../services/queries";
import { colors } from "../../utils";
import IyugaMenu from "../../components/IyugaMenu";
import IyugaDropdown from "../../components/iyugaDropdown";
import UserList from "./components/UserList";

import {
  TAGS,
  MENU_OPTIONS,
  WHEEL_FILTERS,
  CONTENT_TYPE,
  CONTENT_TYPE_FILTERS,
} from "./constants";
import { debounce } from "lodash";
import WheelGridSkeletonLoader from "../../components/WheelGallery/WheelListing/ListComponents/WheelGridSkeletonLoader";
import TagSkeletonLoader from "./components/TagSkeletonLoader";
import CreateWheelPopup from "../../components/CreateWheelPopup";
import UserListSkeletonLoader from "./components/UserListSkeletonLoader";
import ReportModal from "../../components/ReportModal";

/** Reusable Dropdown Button Component */
const DropdownButton = ({ label, icon }) => (
  <Box
    sx={{
      border: `1px solid ${colors.NeutralBorder}`,
      py: "6px",
      px: "12px",
      borderRadius: "6px",
      cursor: "pointer",
    }}
  >
    <Stack direction="row" alignItems="center" gap={1}>
      {icon}
      <div>
        <TextView variant="largeText">{label}</TextView>
      </div>
    </Stack>
  </Box>
);

const SearchBarComponent = ({ searchText, setSearchText }) => {
  return (
    <SearchInput
      inputStyle={{ margin: 0 }}
      isSearch
      variant="secondary"
      containerStyle={{ marginBottom: "0px" }}
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
    />
  );
};

export default function ExplorePage() {
  const [selectedTag, setSelectedTag] = useState(null);
  const [showSearchInAppBar, setShowSearchInAppBar] = useState(false);
  const bannerRef = useRef(null);
  const [selected, setSelected] = useState(MENU_OPTIONS[0].value);
  const [selectedContentType, setSelectedContentType] = useState(
    CONTENT_TYPE_FILTERS[0].value
  );
  const [searchText, setSearchText] = useState("");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const loadMoreRef = useRef(null);
  const [debouncedSearchText, setDebouncedSearchText] = useState(""); // API-triggered state

  const { data: tags = [], isLoading: isTagsLoading } = useTopTags();

  const debounceSearch = useCallback(
    debounce((query) => {
      setDebouncedSearchText(query);
    }, 500), // Adjust debounce time as needed
    []
  );

  useEffect(() => {
    debounceSearch(searchText);
    return () => debounceSearch.cancel(); // Cleanup on unmount
  }, [searchText, debounceSearch]);

  const {
    data: wheelsData,
    fetchNextPage: fetchNextWheelsPage,
    hasNextPage: hasNextWheelsPage,
    isLoading: isWheelsLoading,
    isError: isWheelsError,
    error: wheelsError,
  } = useInfiniteExploreWheels(
    {
      searchedText: debouncedSearchText,
      selectedSort: selected,
      selectedFilters: selectedFilters.length > 0 ? selectedFilters : undefined,
      selectedTag: selectedTag?.id,
    },
    {
      enabled: selectedContentType === CONTENT_TYPE.WHEELS && !!selectedTag?.id,
      // Add stale time and cache time configurations
      staleTime: 0, // Data is immediately considered stale
      cacheTime: 1000 * 60 * 5, // Cache for 5 minutes
    }
  );

  const {
    data: creatorsData,
    fetchNextPage: fetchNextCreatorsPage,
    hasNextPage: hasNextCreatorsPage,
    isLoading: isCreatorsLoading,
    isError: isCreatorsError,
    error: creatorsError,
  } = useInfiniteExploreCreators(
    {
      searchedText: debouncedSearchText,
      selectedFilters: selectedFilters.length > 0 ? selectedFilters : undefined,
      selectedTag: selectedTag?.id,
    },
    {
      enabled: selectedContentType === CONTENT_TYPE.USERS && !!selectedTag?.id,
    }
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          if (
            selectedContentType === CONTENT_TYPE.WHEELS &&
            hasNextWheelsPage &&
            !isWheelsLoading
          ) {
            console.log("fetching next wheels page");
            fetchNextWheelsPage();
          } else if (
            selectedContentType === CONTENT_TYPE.USERS &&
            hasNextCreatorsPage &&
            !isCreatorsLoading
          ) {
            console.log("fetching next creators page");
            fetchNextCreatorsPage();
          }
        }
      },
      { threshold: 0.1 }
    );

    const currentLoadMoreRef = loadMoreRef.current;
    if (currentLoadMoreRef) {
      observer.observe(currentLoadMoreRef);
    }

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [
    hasNextWheelsPage,
    isWheelsLoading,
    fetchNextWheelsPage,
    hasNextCreatorsPage,
    isCreatorsLoading,
    fetchNextCreatorsPage,
    selectedContentType,
  ]);

  useEffect(() => {
    if (tags.length > 0 && !selectedTag) {
      setSelectedTag(tags[0]);
    }
  }, [tags]);

  // Scroll observer to track when the banner is out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowSearchInAppBar(!entry.isIntersecting);
      },
      { threshold: 0.4 }
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      if (bannerRef.current) {
        observer.unobserve(bannerRef.current);
      }
    };
  }, []);

  /** Flatten paginated data */
  const allWheels =
    wheelsData?.pages.flatMap(
      (page) => page.results?.filter((wheel) => wheel.visibility !== 2) || []
    ) || [];
  const allUsers = creatorsData?.pages.flatMap((page) => page.results) || [];

  const ContentTypeDropdown = () => {
    return (
      <IyugaDropdown
        options={CONTENT_TYPE_FILTERS}
        selected={selectedContentType}
        onSelect={(value) => setSelectedContentType(value)}
        label="Select an option"
      />
    );
  };

  return (
    <Box sx={{ backgroundColor: colors.white }}>
      <ExploreAppBar
        SearchBarComponent={
          <SearchBarComponent
            searchText={searchText}
            setSearchText={setSearchText}
          />
        }
        ContentTypeDropdown={ContentTypeDropdown}
        showSearch={showSearchInAppBar}
      />
      <div ref={bannerRef}>
        <ExploreBanner
          SearchBarComponent={
            <SearchBarComponent
              searchText={searchText}
              setSearchText={setSearchText}
            />
          }
          ContentTypeDropdown={ContentTypeDropdown}
          showSearch={!showSearchInAppBar}
        />
      </div>

      <Stack
        sx={{ backgroundColor: colors.white, px: 4 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {selectedContentType === CONTENT_TYPE.WHEELS && (
          <IyugaMenu
            options={MENU_OPTIONS}
            selected={selected}
            onSelect={setSelected}
          >
            <DropdownButton
              label="Sort"
              icon={<Sort width={16} height={16} color="primary" />}
            />
          </IyugaMenu>
        )}

        {/* {isTagsLoading ? (
          <TagSkeletonLoader />
        ) : (
          <ExploreTagList
            tags={tags}
            value={selectedTag}
            onSelect={setSelectedTag}
          />
        )} */}

        {selectedContentType === CONTENT_TYPE.WHEELS && (
          <MultiSelect
            options={WHEEL_FILTERS}
            portal
            selectedValues={selectedFilters}
            onChange={setSelectedFilters}
          >
            <DropdownButton
              label="Filters"
              icon={<FilterIcon width={16} height={16} />}
            />
          </MultiSelect>
        )}
      </Stack>

      {/* Loading & Error Handling */}
      {isWheelsLoading && selectedContentType === CONTENT_TYPE.WHEELS && (
        <Box sx={{ textAlign: "center", py: 4, px: 4 }}>
          <WheelGridSkeletonLoader count={8} />
        </Box>
      )}
      {isCreatorsLoading && selectedContentType === CONTENT_TYPE.USERS && (
        <Box sx={{ textAlign: "center", py: 4, px: 4 }}>
          <UserListSkeletonLoader count={2} />
        </Box>
      )}
      {isWheelsError && selectedContentType === CONTENT_TYPE.WHEELS && (
        <Box sx={{ textAlign: "center", py: 4, color: "red" }}>
          Error: {wheelsError.message}
        </Box>
      )}
      {isCreatorsError && selectedContentType === CONTENT_TYPE.USERS && (
        <Box sx={{ textAlign: "center", py: 4, color: "red" }}>
          Error: {creatorsError.message}
        </Box>
      )}
      {!isWheelsLoading &&
        !isWheelsError &&
        allWheels.length === 0 &&
        selectedContentType === CONTENT_TYPE.WHEELS && (
          <Box sx={{ textAlign: "center", py: 4 }}>No data found</Box>
        )}
      {!isCreatorsLoading &&
        !isCreatorsError &&
        allUsers.length === 0 &&
        selectedContentType === CONTENT_TYPE.USERS && (
          <Box sx={{ textAlign: "center", py: 4 }}>No data found</Box>
        )}

      {selectedContentType === CONTENT_TYPE.WHEELS && (
        <>
          <Grid container spacing={2} sx={{ px: 4, py: 4 }}>
            {allWheels.map((wheel) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={wheel.id}>
                <WheelExploreCard data={wheel} />
              </Grid>
            ))}
          </Grid>

          <Box ref={loadMoreRef} sx={{ px: 4 }}>
            {hasNextWheelsPage && <WheelGridSkeletonLoader count={4} />}
          </Box>
        </>
      )}

      {selectedContentType === CONTENT_TYPE.USERS && (
        <>
          <Grid container sx={{ px: 4, mt: 4 }}>
            {allUsers.map((user) => (
              <Grid item xs={12} key={user.user_id}>
                <UserList user={user} wheels={user.top_wheels} />
              </Grid>
            ))}
          </Grid>
          <Box ref={loadMoreRef} sx={{ px: 4 }}>
            {hasNextCreatorsPage && <UserListSkeletonLoader count={1} />}
          </Box>
        </>
      )}

      <CreateWheelPopup explorePage={true} />
      <ReportModal />
    </Box>
  );
}
